import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

// Currency info structure
interface CurrencyInfo {
  symbol: string;
  name: string;
  rate: number;
  locale: string;
}

// Supported currencies with their symbols and locales
// Exchange rates are fetched from API and cached
export const CURRENCIES: Record<string, Omit<CurrencyInfo, 'rate'> & { rate: number }> = {
  USD: { symbol: '$', name: 'US Dollar', rate: 1, locale: 'en-US' },
  EUR: { symbol: '€', name: 'Euro', rate: 0.92, locale: 'de-DE' },
  GBP: { symbol: '£', name: 'British Pound', rate: 0.79, locale: 'en-GB' },
  IDR: { symbol: 'Rp', name: 'Indonesian Rupiah', rate: 15850, locale: 'id-ID' },
  MYR: { symbol: 'RM', name: 'Malaysian Ringgit', rate: 4.47, locale: 'ms-MY' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', rate: 1.34, locale: 'en-SG' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', rate: 1.53, locale: 'en-AU' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', rate: 1.36, locale: 'en-CA' },
  JPY: { symbol: '¥', name: 'Japanese Yen', rate: 149.50, locale: 'ja-JP' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', rate: 7.24, locale: 'zh-CN' },
  KRW: { symbol: '₩', name: 'South Korean Won', rate: 1320, locale: 'ko-KR' },
  INR: { symbol: '₹', name: 'Indian Rupee', rate: 83.12, locale: 'en-IN' },
  PHP: { symbol: '₱', name: 'Philippine Peso', rate: 55.80, locale: 'en-PH' },
  THB: { symbol: '฿', name: 'Thai Baht', rate: 35.50, locale: 'th-TH' },
  VND: { symbol: '₫', name: 'Vietnamese Dong', rate: 24500, locale: 'vi-VN' },
  BRL: { symbol: 'R$', name: 'Brazilian Real', rate: 4.97, locale: 'pt-BR' },
  MXN: { symbol: 'MX$', name: 'Mexican Peso', rate: 17.15, locale: 'es-MX' },
  RUB: { symbol: '₽', name: 'Russian Ruble', rate: 89.50, locale: 'ru-RU' },
  TRY: { symbol: '₺', name: 'Turkish Lira', rate: 32.10, locale: 'tr-TR' },
  ZAR: { symbol: 'R', name: 'South African Rand', rate: 18.75, locale: 'en-ZA' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', rate: 3.67, locale: 'ar-AE' },
  SAR: { symbol: '﷼', name: 'Saudi Riyal', rate: 3.75, locale: 'ar-SA' },
  NZD: { symbol: 'NZ$', name: 'New Zealand Dollar', rate: 1.67, locale: 'en-NZ' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', rate: 0.88, locale: 'de-CH' },
  SEK: { symbol: 'kr', name: 'Swedish Krona', rate: 10.45, locale: 'sv-SE' },
  NOK: { symbol: 'kr', name: 'Norwegian Krone', rate: 10.65, locale: 'nb-NO' },
  DKK: { symbol: 'kr', name: 'Danish Krone', rate: 6.87, locale: 'da-DK' },
  PLN: { symbol: 'zł', name: 'Polish Zloty', rate: 3.95, locale: 'pl-PL' },
  HKD: { symbol: 'HK$', name: 'Hong Kong Dollar', rate: 7.82, locale: 'zh-HK' },
  TWD: { symbol: 'NT$', name: 'Taiwan Dollar', rate: 31.50, locale: 'zh-TW' },
  CZK: { symbol: 'Kč', name: 'Czech Koruna', rate: 23.50, locale: 'cs-CZ' },
  HUF: { symbol: 'Ft', name: 'Hungarian Forint', rate: 355, locale: 'hu-HU' },
  ILS: { symbol: '₪', name: 'Israeli Shekel', rate: 3.65, locale: 'he-IL' },
  CLP: { symbol: '$', name: 'Chilean Peso', rate: 875, locale: 'es-CL' },
  COP: { symbol: '$', name: 'Colombian Peso', rate: 3950, locale: 'es-CO' },
  PEN: { symbol: 'S/', name: 'Peruvian Sol', rate: 3.72, locale: 'es-PE' },
  ARS: { symbol: '$', name: 'Argentine Peso', rate: 350, locale: 'es-AR' },
  EGP: { symbol: 'E£', name: 'Egyptian Pound', rate: 30.9, locale: 'ar-EG' },
  PKR: { symbol: '₨', name: 'Pakistani Rupee', rate: 278, locale: 'ur-PK' },
  BDT: { symbol: '৳', name: 'Bangladeshi Taka', rate: 110, locale: 'bn-BD' },
  NGN: { symbol: '₦', name: 'Nigerian Naira', rate: 770, locale: 'en-NG' },
  UAH: { symbol: '₴', name: 'Ukrainian Hryvnia', rate: 36.9, locale: 'uk-UA' },
  RON: { symbol: 'lei', name: 'Romanian Leu', rate: 4.57, locale: 'ro-RO' },
  KES: { symbol: 'KSh', name: 'Kenyan Shilling', rate: 153, locale: 'sw-KE' },
  QAR: { symbol: 'ر.ق', name: 'Qatari Riyal', rate: 3.64, locale: 'ar-QA' },
  KWD: { symbol: 'د.ك', name: 'Kuwaiti Dinar', rate: 0.31, locale: 'ar-KW' },
  MAD: { symbol: 'د.م.', name: 'Moroccan Dirham', rate: 10.1, locale: 'ar-MA' },
};


// Map browser locale codes to currencies (fallback)
const LOCALE_TO_CURRENCY: Record<string, string> = {
  'en-US': 'USD', 'en-GB': 'GBP', 'en-AU': 'AUD', 'en-CA': 'CAD',
  'en-NZ': 'NZD', 'en-SG': 'SGD', 'en-IN': 'INR', 'en-PH': 'PHP',
  'en-ZA': 'ZAR', 'de-DE': 'EUR', 'de-AT': 'EUR', 'de-CH': 'CHF',
  'fr-FR': 'EUR', 'fr-CA': 'CAD', 'fr-CH': 'CHF', 'es-ES': 'EUR',
  'es-MX': 'MXN', 'it-IT': 'EUR', 'pt-BR': 'BRL', 'pt-PT': 'EUR',
  'nl-NL': 'EUR', 'nl-BE': 'EUR', 'ja-JP': 'JPY', 'ko-KR': 'KRW',
  'zh-CN': 'CNY', 'zh-TW': 'TWD', 'zh-HK': 'HKD', 'id-ID': 'IDR',
  'ms-MY': 'MYR', 'th-TH': 'THB', 'vi-VN': 'VND', 'ru-RU': 'RUB',
  'tr-TR': 'TRY', 'ar-AE': 'AED', 'ar-SA': 'SAR', 'sv-SE': 'SEK',
  'nb-NO': 'NOK', 'da-DK': 'DKK', 'pl-PL': 'PLN', 'fi-FI': 'EUR',
  'el-GR': 'EUR', 'cs-CZ': 'CZK', 'hu-HU': 'HUF', 'he-IL': 'ILS',
  'ro-RO': 'RON', 'uk-UA': 'UAH',
};

// Detect currency from browser locale (fallback method)
function detectCurrencyFromLocale(): string {
  if (typeof navigator === 'undefined') return 'USD';

  const locale = navigator.language || 'en-US';

  if (LOCALE_TO_CURRENCY[locale]) {
    return LOCALE_TO_CURRENCY[locale];
  }

  const language = locale.split('-')[0];
  const languageMatch = Object.entries(LOCALE_TO_CURRENCY).find(
    ([key]) => key.startsWith(language + '-')
  );

  if (languageMatch) {
    return languageMatch[1];
  }

  return 'USD';
}

// API response types
interface GeolocationResponse {
  success: boolean;
  ip: string;
  country: string | null;
  currency: string;
  exchangeRate: number;
  rateSource: string;
}

interface ExchangeRateResponse {
  success: boolean;
  currency: string;
  rate: number;
  source: string;
  fetchedAt: string | null;
  expiresAt: string | null;
  isCached: boolean;
}

interface CurrencyState {
  // The currency code (e.g., 'USD', 'IDR')
  currency: string;
  // The detected currency from IP/locale
  detectedCurrency: string;
  // The detected country code
  detectedCountry: string | null;
  // Whether user has manually selected a currency
  isManuallySet: boolean;
  // Current exchange rate (USD-based)
  exchangeRate: number;
  // When the rate was last fetched
  rateUpdatedAt: string | null;
  // Rate source (api, fallback, cache)
  rateSource: string | null;
  // Whether detection has been done
  isDetected: boolean;
  // Loading state
  isLoading: boolean;
  // Error state
  error: string | null;

  // Actions
  setCurrency: (currency: string) => void;
  resetToDetected: () => void;
  detectFromIP: () => Promise<void>;
  fetchExchangeRate: (currency: string) => Promise<number>;
  convertFromUSD: (amountUSD: number) => number;
  formatPrice: (amountUSD: number, showOriginal?: boolean) => string;
  getCurrencyInfo: () => CurrencyInfo;
}

export const useCurrencyStore = create<CurrencyState>()(
  persist(
    (set, get) => {
      const localeCurrency = detectCurrencyFromLocale();

      return {
        currency: localeCurrency,
        detectedCurrency: localeCurrency,
        detectedCountry: null,
        isManuallySet: false,
        exchangeRate: CURRENCIES[localeCurrency]?.rate || 1,
        rateUpdatedAt: null,
        rateSource: null,
        isDetected: false,
        isLoading: false,
        error: null,

        setCurrency: (currency: string) => {
          if (CURRENCIES[currency]) {
            const currencyInfo = CURRENCIES[currency];
            set({
              currency,
              isManuallySet: currency !== get().detectedCurrency,
              exchangeRate: currencyInfo.rate,
            });

            // Fetch live exchange rate in background
            get().fetchExchangeRate(currency);
          }
        },

        resetToDetected: () => {
          const { detectedCurrency, detectedCountry } = get();
          set({
            currency: detectedCurrency,
            isManuallySet: false,
          });

          // If we have a detected country, refetch the rate
          if (detectedCountry) {
            get().fetchExchangeRate(detectedCurrency);
          }
        },

        detectFromIP: async () => {
          set({ isLoading: true, error: null });

          try {
            const response = await api.get<GeolocationResponse>('/geolocation/detect');
            // The API returns data directly in the response object
            const data = response as unknown as GeolocationResponse;

            if (data.success && data.ip) {
              const currency = data.currency && CURRENCIES[data.currency] ? data.currency : 'USD';

              set({
                detectedCurrency: currency,
                detectedCountry: data.country,
                exchangeRate: data.exchangeRate || CURRENCIES[currency]?.rate || 1,
                rateSource: data.rateSource || 'api',
                rateUpdatedAt: new Date().toISOString(),
                isDetected: true,
                isLoading: false,
              });

              // Only update current currency if not manually set
              if (!get().isManuallySet) {
                set({ currency });
              }
            } else {
              // Fall back to locale detection
              const fallbackCurrency = detectCurrencyFromLocale();
              set({
                detectedCurrency: fallbackCurrency,
                isDetected: true,
                isLoading: false,
              });

              if (!get().isManuallySet) {
                set({ currency: fallbackCurrency });
              }
            }
          } catch (error) {
            console.warn('IP detection failed, using locale fallback:', error);

            const fallbackCurrency = detectCurrencyFromLocale();
            set({
              detectedCurrency: fallbackCurrency,
              isDetected: true,
              isLoading: false,
              error: 'Failed to detect location, using browser locale',
            });

            if (!get().isManuallySet) {
              set({ currency: fallbackCurrency });
            }
          }
        },

        fetchExchangeRate: async (currency: string): Promise<number> => {
          if (currency === 'USD') {
            set({ exchangeRate: 1, rateSource: 'base', rateUpdatedAt: new Date().toISOString() });
            return 1;
          }

          try {
            const response = await api.get<ExchangeRateResponse>(`/geolocation/rate/${currency}`);

            if (response.success) {
              const data = response as unknown as ExchangeRateResponse;
              const rate = data.rate;

              // Update CURRENCIES cache with live rate
              if (CURRENCIES[currency]) {
                CURRENCIES[currency].rate = rate;
              }

              // Update store if this is the current currency
              if (get().currency === currency) {
                set({
                  exchangeRate: rate,
                  rateSource: data.source,
                  rateUpdatedAt: data.fetchedAt || new Date().toISOString(),
                });
              }

              return rate;
            }
          } catch (error) {
            console.warn(`Failed to fetch exchange rate for ${currency}:`, error);
          }

          // Return fallback rate
          return CURRENCIES[currency]?.rate || 1;
        },

        convertFromUSD: (amountUSD: number): number => {
          const { exchangeRate } = get();
          return amountUSD * exchangeRate;
        },

        formatPrice: (amountUSD: number, showOriginal = false): string => {
          const { currency, exchangeRate } = get();
          const currencyInfo = CURRENCIES[currency];

          if (!currencyInfo) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(amountUSD);
          }

          const convertedAmount = amountUSD * exchangeRate;

          const formatted = new Intl.NumberFormat(currencyInfo.locale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: exchangeRate > 100 ? 0 : 2,
            maximumFractionDigits: exchangeRate > 100 ? 0 : 2,
          }).format(convertedAmount);

          if (showOriginal && currency !== 'USD') {
            const usdFormatted = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(amountUSD);
            return `${formatted} (${usdFormatted})`;
          }

          return formatted;
        },

        getCurrencyInfo: (): CurrencyInfo => {
          const { currency, exchangeRate } = get();
          const info = CURRENCIES[currency];
          return {
            symbol: info?.symbol ?? '$',
            name: info?.name ?? 'US Dollar',
            locale: info?.locale ?? 'en-US',
            rate: exchangeRate,
          };
        },
      };
    },
    {
      name: 'currency-preferences',
      partialize: (state) => ({
        currency: state.currency,
        isManuallySet: state.isManuallySet,
        detectedCurrency: state.detectedCurrency,
        detectedCountry: state.detectedCountry,
      }),
    }
  )
);

// Export list of available currencies for the selector
export const AVAILABLE_CURRENCIES = Object.entries(CURRENCIES).map(([code, info]) => ({
  code,
  ...info,
}));

// Hook to initialize currency detection on app load
export function initializeCurrencyDetection(): void {
  const store = useCurrencyStore.getState();

  // Only detect if not already detected
  if (!store.isDetected) {
    store.detectFromIP();
  } else if (!store.isManuallySet) {
    // Refresh exchange rate for current currency
    store.fetchExchangeRate(store.currency);
  }
}
